import { forEach, map, difference, assign } from 'lodash';

import { Base, clone } from 'gear';

import { Point, Matrix, Frame, Figures } from 'cdl';

import { SelectHandles } from './select-handles';

const _abs = Math.abs;

const { dotProduct, substract } = Point;

export function computeScale( pin, dir, mousePos, initialMousePos, zoomScale ) {

  const initialDiff = substract(initialMousePos,pin);

  const currentDiff = substract(mousePos,pin);

  const dpi = dotProduct(initialDiff,dir);

  let dpc = dotProduct(currentDiff,dir);
  if( _abs(dpc) < 8 * zoomScale ) {
    if( _abs(dpi) < 8 * zoomScale ) {
      dpc = dpi ;
    }
    else {
      dpc = 8 * zoomScale * ((dpc<0)?-1:1) ;
    }
  }

  return {
    pin: pin.clone(),
    sx: dpc / dpi,
    deg: dir.angle()
  };
}

function computeShear( pin, dir, mousePos, initialMousePos ) {
  // http://www.w3.org/TR/SVG11/coords.html
  // http://jsfiddle.net/jbpEk/
  // http://jsfiddle.net/jbpEk/26/

  const initialDiff = substract(initialMousePos,pin);
  const currentDiff = substract(mousePos       ,pin);
  const dpc = dotProduct(currentDiff,dir) ;
  const dpi = dotProduct(initialDiff,dir) ;

  let dpcPa = _abs( dotProduct(initialDiff,dir.perpendicular(-1)) ) ;

  if( dpcPa < 8 ) {
    dpcPa = 8;
  }

  const r = {
    deg: dir.angle(),
    sx: (dpc-dpi)/dpcPa,
    sy: 0,
    pin: pin.clone()
  };

  return r;
}

// Computes the rotation in radians.
function computeRotation( pin, mousePos) {
  return mousePos. minus( pin ) .angle();
}

function computeFiguresFrame_( figures ) {
  if( figures.length === 1 ) {
    return figures[0].frame_();
  }
  return Frame.fromBounds( Figures.bounds_(figures) );
}

function figuresSnapToPoints( view, editingFigures ) {

  const snapTo = [];

  const viewBounds = view.bounds;
  forEach( view.horizontalUserLines, userLine => {
    if( userLine._snap ) {
      const snapPoint = new Point(userLine._position,viewBounds.ly);
      snapTo.push({ lx_ly: snapPoint, center: snapPoint, hx_hy: snapPoint });
    }
  });
  forEach( view.verticalUserLines, userLine => {
    if( userLine._snap ) {
      const snapPoint = new Point(viewBounds.lx,userLine._position);
      snapTo.push({ lx_ly: snapPoint, center: snapPoint, hx_hy: snapPoint });
    }
  });

  snapTo.push({ center: Figures.bounds_(editingFigures).center });

  forEach( difference(view.activePageFigures,editingFigures), figure => {
    const { lx_ly, center, hx_hy } = figure.frame_().bounds;
    snapTo.push({ lx_ly, center, hx_hy });
  });

  return snapTo;
}


// We should divide this into several helpers

const FrameInteractionMixin = {

  // expects
  //
  //  members:
  //    view, handles
  //
  //  functions:
  //    editingItems(view) => items
  //    getOriginalTransforms(items) => transforms
  //    setOriginalTransforms(items,transforms)
  //    transformItems(items,transform)
  //
  //  optional:
  //    setOriginalPositions(items,transforms)
  //    translateItems(items,offset)
  //    snapToPoints(view,items) => [ { lx_ly, center, hx_hy }, ... ]
  //    transformWithFixedCenter(view,items) => boolean
  //    scaleOriginalXY(view,items) => { scaleX : s00, scaleY : s11 }
  //    setIddleToolhelp()

  initFrameInteraction() {
    assign( this, {
      _data: null,
      _editingItems: null,
      _originalTransforms: null,
      _editionMode: false,
      _downFrame: null
    });
  },

  snapToPoints( /*view,items*/) {
    return [];
  },
  transformWithFixedCenter( /*view,items*/) {
    return false;
  },
  scaleOriginalXY( /*view,items*/) {
    return null; // { scaleX : s00, scaleY : s11 };
  },

  setIddleToolhelp() { },
  setEditionToolhelp( transformType, shiftToolhelp, shiftOnlyForSingleSelection ) {
    const { tool, _editingItems } = this;
    const dragText = 'Drag to '+transformType+' selection';
    const altText = 'Alt to make selection transparent';
    if( _editingItems.length > 1 ) {
      tool.toolhelp( dragText + (shiftOnlyForSingleSelection ? '':('\n'+shiftToolhelp)), altText );
    }
    else {
      tool.toolhelp( dragText, shiftToolhelp, altText );
    }
  },

  onItemsDrag( /*event, items*/ ) { },
  onItemsDrop( /*event, items*/ ) {
    return true;
  },

  // Internals
  //--------------------------------------------------------------------------------------

  startEditionMode( transformType, shiftToolhelp, shiftOnlyForSingleSelection ) {
    const { view, handles } = this;

    const editingItems = this._editingItems = clone(this.editingItems(view));
    this._originalTransforms = this.getOriginalTransforms(editingItems);

    this._downFrame = handles._frame;
    this._editionMode = true;

    this.setEditionToolhelp(transformType,shiftToolhelp,shiftOnlyForSingleSelection);
    view.redraw('annotations');
  },

  endEditionMode() {
    this.setIddleToolhelp();
    this.initFrameInteraction();
  },

  // optional
  translateItems( items, offset ) {
    this.transformItems(items,Matrix.translate(offset));
  },

  setOriginalPositions( figures, originalTransforms ) {
    this.setOriginalTransforms(figures,originalTransforms);
  },

  updateHandlesFrame( transform ) {
    const { view, handles, _downFrame } = this;
    handles.update( _downFrame.transformed(transform) );
    // Batch redraw, the frame will be drawn again at the same time as the document
    view.batchRedraw('tools');
  },

  absoluteTransform( transform ) {
    const { view, _editingItems, _originalTransforms } = this;
    this.setOriginalTransforms(_editingItems,_originalTransforms);
    this.updateHandlesFrame(transform);
    this.transformItems(_editingItems,transform);
    view.preview$();
  },

  absoluteTranslate( offset ) {
    const { view, _editingItems, _originalTransforms } = this;
    this.setOriginalPositions(_editingItems,_originalTransforms);
    this.updateHandlesFrame( Matrix.translate(offset) );
    this.translateItems(_editingItems,offset);
    view.preview$();
  },

  commit$() {
    this.view.commit$();
  },

  // Translate
  //-------------------------------------------------------------------------------------

  onTranslateStart({ view }) {
    this._data = {
      snapLock: view.snapLock,
      offset: new Point(0,0),
      started: false,
      snapTo: this.snapToPoints(view,this.editingItems(view)), // [ { lx_ly, center, hx_hy }, ... ]
    };
  },

  // Use docPointRaw that allows jumps depending on the view used to see
  // the document (Canvas3D, TextureView)

  getOnTranslateValue({ view, docPointRaw, cmdKey, shiftKey }) {
    const { handles, _data, _downFrame } = this;

    const offset = substract( docPointRaw, handles.mouseDownPos );
    if( ! offset.isZero() ) {
      _data.started = true;
    }

    const snapTo = _data.snapTo;
    // [TODO] Move AlignmentLines logic to the plugin
    const alignmentLines = view._alignmentLines || { _visible: false, _snap: false };

    const forceSnap = shiftKey || cmdKey || _data.snapLock;
    const al_visible = forceSnap || alignmentLines._visible;
    const al_snap    = forceSnap || alignmentLines._snap;
    const al_enabled = al_visible || al_snap;
    if( snapTo && al_enabled && _data.started ) { // snap
      const snapSize = view.viewDeltaToDoc(14);
      const { bounds } = _downFrame.translated(offset);
      let al_x = null, al_y = null, diff_x = Number.MAX_VALUE, diff_y = Number.MAX_VALUE;
      forEach( snapTo, snap => {
        forEach( snap, (s,name) => {

          const diff = substract( s, bounds[name] );

          const ax = _abs(diff.x);
          if( ax < snapSize && _abs(diff_x) > ax ) {
            diff_x = diff.x;
            if( al_visible ) {
              al_x = s.x;
            }
          }
          const ay = _abs(diff.y);
          if( ay < snapSize && _abs(diff_y) > ay ) {
            diff_y = diff.y;
            if( al_visible ) {
              al_y = s.y;
            }
          }
        });
      });

      handles.guideline_x = al_x;
      if( al_snap && diff_x !== Number.MAX_VALUE ) {
        offset.x += diff_x;
      }

      handles.guideline_y = al_y;
      if( al_snap && diff_y !== Number.MAX_VALUE ) {
        offset.y += diff_y;
      }
    }
    else {
      handles.guideline_x = null;
      handles.guideline_y = null;
    }

    return view.asFastOffset(offset);
  },

  onTranslateMove( event ) {
    const { _data, _editionMode, _editingItems } = this;
    if( ! _editionMode ) {
      this.startEditionMode('translate','Shift to show snap guidelines');
    }
    const offset = _data.offset = this.getOnTranslateValue(event);
    this.absoluteTranslate( offset );
    this.onItemsDrag(event,_editingItems);
  },

  onTranslateEnd( event ) {
    const { _data, _editionMode, _editingItems, handles } = this;
    if( _editionMode ) {
      const { offset } = _data;

      const needsRedraw = ( handles.guideline_x || handles.guideline_y );

      handles.guideline_x = null;
      handles.guideline_y = null;

      this.endEditionMode();

      if( ( offset.x !== 0 || offset.y !== 0 ) && this.onItemsDrop(event,_editingItems) ) {
        this.commit$();
      }
      else if( needsRedraw ) {
        this.tool.redraw();
      }

    }
  },

  // Scale
  //-------------------------------------------------------------------------------------

  onScaleStart({ view }) {
    const { handles } = this;
    const editingItems = this.editingItems(view);
    const pin = handles.current.pin.pos;
    this._data = {
      snapLock: view.snapLock,
      scale       : { pin: pin, sx:1, sy:1, deg: 0 } ,
      fixedCenter : this.transformWithFixedCenter( view, editingItems ),
      center      : handles.center,
      scalePin    : pin,
      scaleDir    : handles.scaleDirFromHandle( handles.current ),
      os          : this.scaleOriginalXY( view, editingItems )
    };
  },

  getOnScaleValue({ view, event, docPoint, cmdKey, shiftKey }) {
    const { handles, _data } = this;
    const { current, mouseDownPos } = handles;
    const { fixedCenter, center, scalePin, scaleDir } = _data;

    const pin = ( fixedCenter || cmdKey ) ? center : scalePin;

    const scale = computeScale( pin, scaleDir, docPoint, mouseDownPos, view.viewDeltaToDoc(1) );

    const { name } = current;
    scale.sy = ( name === 'ne' || name === 'nw' || name === 'sw' || name === 'se' ) ? scale.sx : 1;

    const { os } = _data;
    if( shiftKey && os ) { // Snapping to sx = sy
      if( name === 'n' || name === 's') {
        if( _abs( (os.scaleY * scale.sx) / os.scaleX - 1 ) < 0.15 ) {
          scale.sx = os.scaleX / os.scaleY ;
        }
      }
      else if( name === 'e' || name === 'w' ) {
        if( _abs( (os.scaleX * scale.sx) / os.scaleY - 1 ) < 0.15 ) {
          scale.sx = os.scaleY / os.scaleX ;
        }
      }
    }
    return scale;
  },
  onScaleMove( event ) {
    const { _editionMode } = this;
    if( ! _editionMode ) {
      this.startEditionMode('scale','Shift to lock to on isometric scaling',true);
    }

    this.doScaleMove(event);
  },

  doScaleMove( event ) {
    const { _data } = this;

    const { pin, sx, sy, deg } = _data.scale = this.getOnScaleValue(event);

    const scaleFrom = Matrix.scaleFrom( pin, sx, sy, deg );

    this.absoluteTransform( scaleFrom );
  },

  onScaleEnd() {
    const { _data, _editionMode } = this;
    if( _editionMode ) {
      const { sx } = _data.scale;

      this.endEditionMode();
      if( sx !== 1 ) {
        this.commit$();
      }
    }
  },

  // Rotate
  //-------------------------------------------------------------------------------------

  onRotateStart({ view, event, docPoint }) {
    const { handles } = this;
    const { center, current } = handles;

    this._data = {
      snapLock: view.snapLock,
      deg: 0,
      center,
      initialDeg: computeRotation(center,docPoint),
      initialSnapDeg: computeRotation(center,current.pos)
    };
  },
  getOnRotateValue({ event, docPoint, shiftKey, cmdKey }) {
    const { _data } = this;
    const { center, initialDeg, initialSnapDeg } = _data;

    const r = computeRotation(center,docPoint);
    if( shiftKey || cmdKey ) { // Snap
      const sm = 15;
      return Math.round(r/sm) * sm - initialSnapDeg;
    }
    else {
      return r - initialDeg;
    }
  },
  onRotateMove( event ) {
    const { _editionMode, _data } = this;

    if( ! _editionMode ) {
      this.startEditionMode('rotate','Shift to lock rotation on multiples of 15 degrees');
    }

    const deg = _data.deg = this.getOnRotateValue(event);
    const rotateAround = Matrix.rotateAround(_data.center,deg);

    this.absoluteTransform( rotateAround );
  },
  onRotateEnd() {
    const { _editionMode, _data } = this;

    if( _editionMode ) {
      const { deg } = _data;
      this.endEditionMode();
      if( deg !== 0 ) {
        this.commit$();
      }
    }
  },

  // Shear
  //-------------------------------------------------------------------------------------

  onShearStart({ view }) {
    const { handles } = this;
    const { current, center } = handles;

    this._data = {
      snapLock: view.snapLock,
      shear           : { sx: 0, sy: 0 },
      center          ,
      fixedCenter     : this.transformWithFixedCenter(view,this.editingItems(view)),
      shearPin        : current.pin.pos,
      shearDir        : handles.shearDirFromHandle(current),
      shearHandlePos  : current.pos,
      shearOppPin     : current.oppPin.pos
    };
  },

  getOnShearValue({ view, event, docPoint }) {
    const { handles, _data } = this;
    const { ctrlKey, shiftKey } = handles;
    const { fixedCenter, center, shearPin, shearOppPin,
            shearHandlePos, shearDir } = _data;

    let downPos = handles.mouseDownPos;

    let pos = docPoint;

    const pin = ( fixedCenter || ctrlKey ) ? center : shearPin;
    if( shiftKey ) { // Snap
      const currentOppPin = pos .plus( substract( shearOppPin, shearHandlePos ) );
      const kp = substract( currentOppPin, pin );
      const dp = dotProduct( kp, shearDir );
      if( _abs(dp) < 15 ) {
        pos = pin .plus( substract( shearHandlePos, shearOppPin ) );
        downPos = shearHandlePos;
      }
    }

    return computeShear(pin, _data.shearDir, pos, downPos);
  },

  onShearMove( event ) {
    const { _data, _editionMode } = this;

    if( ! _editionMode ) {
      this.startEditionMode('skew','Shift to lock when there is no skewing',true);
    }
    const { pin, sx, sy, deg } = _data.shear = this.getOnShearValue(event);
    const shearAround = Matrix.shearAround( pin, sx, sy, deg );

    this.absoluteTransform( shearAround );
  },

  onShearEnd() {
    const { _editionMode, _data } = this;
    if( _editionMode ) {
      const { sx, sy } = _data.shear;
      this.endEditionMode();
      if( sx !== 0 || sy !== 0 ) {
        this.commit$();
      }
    }
  }
};

export const SelectAndTransformHandles = Base.extend( FrameInteractionMixin, {

  // To overwrite in the config

  // Check FrameInteractionMixin plus...
  //
  // functions:
  //   computeItemsFrame_(items) => frame
  //   itemsEnabledTransforms(items) => { anisometricTransforms, translate, scale, rotate, shear, contentEdit }
  //
  // optional:
  //   invalidatedItems(items) => boolean


  init( config ) {
    this.handles = null;
    assign(this,config);
    this.initFrameInteraction();
  },

  isHandle( event ) {
    return this.handles ? this.handles.isHandle(event) : false;
  },

  isHandleEnabledAt( event ) {
    return this.handles ? this.handles.isHandleEnabledAt(event) : false;
  },

  isPadHandle( event ) {
    return this.handles ? this.handles.isPadHandle(event) : false;
  },

  get frame() {
    return this.handles ? this.handles._frame : null;
  },

  get visible() {
    return this.handles ? this.handles.visible : false;
  },

  down( event ) {
    if( this.handles ) {
      this.handles.down(event);
    }
  },

  mousemove( event ) {
    if( this.handles ) {
      this.handles.mousemove(event);
    }
  },

  move( event ) {
    if( this.handles ) {
      this.handles.move(event);
    }
  },

  up( event ) {
    if( this.handles ) {
      this.handles.up(event);
    }
  },

  draw( config ) {
    if( this.handles ) {
      this.handles.draw(config);
    }
  },

  create() {
    if( this.handles ) {
      this.dispose();
    }
    this.handles = new SelectHandles({ view: this.view });
    this.handles.on({ 'rotatestart'    : this.onRotateStart,
                      'rotatemove'     : this.onRotateMove,
                      'rotateend'      : this.onRotateEnd,
                      'scalestart'     : this.onScaleStart,
                      'scalemove'      : this.onScaleMove,
                      'scaleend'       : this.onScaleEnd,
                      'shearstart'     : this.onShearStart,
                      'shearmove'      : this.onShearMove,
                      'shearend'       : this.onShearEnd,
                      'translatestart' : this.onTranslateStart,
                      'translatemove'  : this.onTranslateMove,
                      'translateend'   : this.onTranslateEnd,
                      'edit'           : this.onContentEdit }, this );
  },

  dispose() {
    if( this.handles ) {
      this.handles.un(this);
      this.handles = null;
    }
  },

  onContentEdit() {
    const { view } = this;
    const items = this.editingItems(view);
    if( items.length === 1 ) {
      view.editContent( items[0] );
    }
  },

  invalidatedItems( /*items*/) {
    return false;
  },

  update( frame ) {
    const { view, handles } = this;

    if( ! handles ) {
      return;
    }

    if( frame ) {
      handles.update(frame);
    }
    else {

      const items = this.editingItems(view);
      if( items.length > 0 ) {

        // If edition mode is on, the frame is already updated while transforming
        if( ! this._editionMode && ! this.invalidatedItems(items) ) {

          const config = this.itemsEnabledTransforms(view,items);

          handles.anisometricTransformsEnabled = config.anisometricTransforms || false;

          handles.translateEnabled = config.translate || false;
          handles.scaleEnabled = config.scale || false;
          handles.rotateEnabled = config.rotate || false;
          handles.shearEnabled = config.shear || false;

          handles.editEnabled = config.contentEdit || false;

          handles.resize( this.computeItemsFrame_(items) );
          handles.visible = true;
        }
      }
      else {
        handles.visible = false;
      }
    }

    this.view.batchRedraw('tools');
  }
});

export const FiguresHandles = SelectAndTransformHandles.extend({

  getOriginalTransforms( figures ) {
    return map( figures, figure => {
      return {
        matrix22: figure.matrix22.clone(),
        pin: figure.pin.clone()
      };
    });
  },

  setOriginalTransforms( figures, originalTransforms ) {
    forEach( figures, (figure,n) => {
      const original = originalTransforms[n];
      figure.pin = original.pin.clone();
      figure.matrix22 = original.matrix22.clone();
    });
  },

  itemsEnabledTransforms( view, figures ) {
    return {
      anisometricTransforms: view.are(figures,'aspectRatioChangeable'),

      translate: view.are(figures,'movable'),
      scale: view.are(figures,'scalable'),
      rotate: view.are(figures,'rotatable'),
      shear: view.are(figures,'skewable'),

      contentEdit: this.itemsEnabledContentEdit(view,figures)
    };
  },

  itemsEnabledContentEdit(view,figures) {
    return view.are(figures,'contentEditable');
  },

  transformItems: Figures.transform,
  invalidatedItems: Figures.invalidated,

  computeItemsFrame_: computeFiguresFrame_,

  snapToPoints: figuresSnapToPoints,

  transformWithFixedCenter( view, figures ) {
    return ! view.are(figures,'movable');
  },

  scaleOriginalXY( view, items ) {
    const figure = items[0];
    const matrix = figure._matrix22;
    const urm = Matrix.m( Matrix.rotate( -matrix.angle() ), matrix );
    return { scaleX : urm.s00, scaleY : urm.s11 };
  }
});

// Attempt to avoid an extra Process call to measure ProxyGroups after
// isometric scaling (pr)

export const OptimizedFiguresHandles = FiguresHandles.extend({

  translateItems: Figures.fastTranslate_, // forced, offset is already fixed

  getOriginalTransforms( figures ) {
    return map( figures, figure => {
      return {
        matrix22: figure.matrix22.clone(),
        pin: figure.pin.clone(),
    //  cache: figure._cache.clone()  (pr)
      };
    });
  },

  setOriginalPositions( figures, originalTransforms ) {
    forEach( figures, (figure,n) => {
      const original = originalTransforms[n];
      figure.fastTranslate( original.pin .minus( figure.pin ) );
    });
  },

  // Do it by hand, avoid need to compute bounds for proxy groups
  doScaleMove( event ) {
    const { _data } = this;

    const lastScale = _data.scale;

    const { pin, sx, sy, deg } = _data.scale = this.getOnScaleValue(event);

    const scaleFrom = Matrix.scaleFrom( pin, sx, sy, deg );

    const { view, _editingItems } = this;
    this.updateHandlesFrame(scaleFrom);

    // Revert last change and re-apply absolute scaling, caches are kept
    Figures.scaleFrom(_editingItems,lastScale.pin,1/lastScale.sx,1/lastScale.sy,lastScale.deg);
    Figures.scaleFrom(_editingItems,pin,sx,sy,deg);

    view.preview$();
  }

});



/** WEBPACK FOOTER **
 ** ./src/ui/tool/select-transform.js
 **/